import { domainToASCII } from 'url'

export const capitalize = (str: string): string => {
  return str && str.charAt(0).toUpperCase() + str.slice(1)
}

export const capitalizeEachWord = (str: string): string => {
  return str
    .split(' ')
    .map(word => capitalize(word))
    .join(' ')
}

/**
 * Normalize a string by trimming and converting to lowercase
 */
export const normalizeString = (value: string): string => value.trim().toLowerCase()

/**
 * Convert string to slug-safe string.
 * @param {string} args
 * @returns {string}
 */
export const slugify = (...args: string[]): string => {
  const value = args.join(' ')

  return (
    value
      ?.normalize('NFD') // split an accented letter in the base letter and the accent
      .replaceAll(/[\u0300-\u036F]/g, '') // remove all previously split accents
      .toLowerCase()
      .trim()
      .replaceAll(/[^a-z0-9 ]/g, '') // remove all chars not letters, numbers and spaces (to be replaced)
      .replaceAll(/\s+/g, '-') ?? '' // separator
  )
}

export function isEnumMember<E extends string>(
  value: unknown,
  enumObject: Record<string, E>,
): value is E {
  return Object.values(enumObject).includes(value as E)
}

/**
 * Generate a random key for React elements.
 * Uses Math.random() to create a simple alphanumeric string.
 * @returns {string} Random key string
 */
export const generateRandomKey = (): string => {
  return Math.random().toString(36).slice(2, 11)
}

// Copied from Coda's js-core/utils/string.ts
const PluralAddEsWords = new Set(['canvas'])
const SingularSameAsPluralWords = new Set(['analytics'])

/**
 * Pluralize a phrase based on count.
 * Handles special cases like "canvas" -> "canvases" and "analytics" (same singular/plural).
 * Copied from Coda's implementation.
 */
export function pluralize(phrase: string, count: number = 2): string {
  const words = phrase.split(' ')
  const finalWord = words.at(-1)
  if (!finalWord) {
    return phrase
  }
  const finalWordLower = finalWord.toLowerCase()
  if (Math.abs(count) !== 1 && PluralAddEsWords.has(finalWordLower)) {
    return `${phrase}es`
  }
  return `${phrase}${Math.abs(count) === 1 || SingularSameAsPluralWords.has(finalWordLower) ? '' : 's'}`
}

/**
 * Get displayable version of a network domain, showing both Unicode and ASCII versions if different.
 * For example: "münchen.de" becomes "münchen.de (xn--mnchen-3ya.de)"
 *
 * Based on Coda's browser-shared/packs/helpers.tsx implementation,
 * but using Node.js url module's domainToASCII instead of deprecated punycode.
 *
 * Note: In Node.js, URL constructor converts Unicode to punycode automatically,
 * but we use domainToASCII for explicit conversion to match Coda's behavior exactly.
 */
export function getDisplayableNetworkDomain(networkDomain: string): string {
  try {
    // Use Node.js url module's domainToASCII for explicit punycode conversion
    // This is the modern replacement for punycode.toASCII()
    const asciiDomain = domainToASCII(networkDomain)

    if (asciiDomain === networkDomain) {
      return networkDomain
    }

    return `${networkDomain} (${asciiDomain})`
  } catch {
    // If conversion fails, return original domain
    return networkDomain
  }
}
